import get from "lodash.get";
import PropTypes from "prop-types";

const canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);
export const sharePageUrl = canUseDOM && window.location.href;

export function truncate(string = "", limit = 60, ellipsis = true) {
  const putEllipsis = ellipsis ? " ..." : "";
  return typeof string === "string" && string.length > limit ? string.substring(0, limit) + putEllipsis : string;
}

const timestampToFormat = (timestamp, showTime = false) => {
  const monthList = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dateTime = new Date(timestamp);
  const date = dateTime
    .getDate()
    .toString()
    .padStart(2, 0);
  const month = monthList[dateTime.getMonth()];
  const year = dateTime.getFullYear();
  const time = formatAMPM(timestamp);
  const renderTime = showTime ? `, ${time}` : "";

  return `${date} ${month}, ${year} ${renderTime}`;
};

export function formatter(value, unit, suffix, date) {
  const showDateForUnits = ["day", "week", "month", "year"];

  if ((unit === "hour" && value > 23) || showDateForUnits.indexOf(unit) !== -1) {
    return timestampToFormat(date);
  } else if (value > 1) {
    return `${value} ${unit}s ${suffix}`;
  }

  return `${value} ${unit} ${suffix} `;
}

function formatAMPM(timestamp) {
  const dateTime = new Date(timestamp);
  let hours = dateTime.getHours();
  let minutes = dateTime.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours || 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  const strTime = hours + ":" + minutes + " " + ampm;
  return strTime;
}

export const hexToRGB = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

export const getLuminanceOfColor = (hex) => {
  const { r, g, b } = hexToRGB(hex);
  return r * 0.299 + g * 0.587 + b * 0.114;
};

export const rgbToHex = (color = "rgba(255, 255, 255, 0)") => {
  let rgba = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return rgba && rgba.length === 4
    ? "#" +
        ("0" + parseInt(rgba[1], 10).toString(16)).slice(-2) +
        ("0" + parseInt(rgba[2], 10).toString(16)).slice(-2) +
        ("0" + parseInt(rgba[3], 10).toString(16)).slice(-2)
    : color;
};

const clientWidths = {
  mobile: 767,
  tablet: 1023
};

export const clientWidth = (device) => {
  const foundDeviceWidth = get(clientWidths, device);
  if (foundDeviceWidth) {
    return get(global, "document.documentElement.clientWidth") <= foundDeviceWidth;
  }
  return false;
};

export const getTextColor = (theme = "rgb(255,255,255)") => {
  const isRegexCheck = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(theme);
  const getHexValue = !isRegexCheck ? rgbToHex(theme) : theme;
  const textColor = getLuminanceOfColor(getHexValue) < 155 ? "light" : "dark";
  return textColor;
};

export function getNumberOfStoriesToShow(numberOfStoriesToShow) {
  return numberOfStoriesToShow ? parseInt(numberOfStoriesToShow) : 1;
}

export const getSlot = (type, component, storyCallback, collectionCallback) => {
  switch (type) {
    case "story":
      return storyCallback();
    case "collection":
      return collectionCallback();
    default:
      return component ? component() : null;
  }
};

export const getSlug = (collection, config) => {
  const isSection = collection.template === "section";
  if (isSection) {
    const sectionId = get(collection, ["metadata", "section", "0", "id"], "");
    const { sections } = config;
    const section = sections && sections.find((section) => section.id === sectionId);
    return section ? section["section-url"] : "/";
  }
  return `/collection/${collection.slug}`;
};

export function isExternalStory(story = {}) {
  return get(story, ["metadata", "reference-url"], "");
}

// Adds target='_blank' attribute to anchor tags if not present
export const updateContentLinks = (content) => {
  let updatedContent = content;
  const anchorTagsList = content.match(/(<a).*?(>)/g);
  anchorTagsList &&
    anchorTagsList.forEach((anchorTag) => {
      const isTargetPresent = /target=/g.test(anchorTag);
      if (!isTargetPresent) {
        updatedContent = updatedContent.replace(anchorTag, `<a target='_blank'${anchorTag.substring(2)}`);
      }
    });
  return updatedContent;
};

export const isEmpty = (value) => {
  return (
    value == null || // From standard.js: Always use === - but value == null is allowed to check null || undefined
    (typeof value === "object" && Object.entries(value).length === 0 && value.constructor === Object) ||
    (typeof value === "string" && value.trim().length === 0)
  );
};

export function dateFormatter(value, unit, suffix, date) {
  return timestampToFormat(date, true);
}

export const shapeConfig = PropTypes.shape({
  "sketches-host": PropTypes.string,
  "cdn-name": PropTypes.string,
  "cdn-image": PropTypes.string
});

export const shapeStory = PropTypes.shape({
  headline: PropTypes.string,
  "last-published-at": PropTypes.string,
  subheadline: PropTypes.string
});

export const getStoryUrl = (story, defaultValue = "") => {
  return get(story, ["story-template"]) === "news-elsewhere"
    ? get(story, ["metadata", "reference-url"], "")
    : story.url || defaultValue;
};
